import { initializeApp, getApps, deleteApp, type FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  type Firestore,
} from 'firebase/firestore';
import type { ClientFirebaseConfig } from '../types';

const CLIENT_APP_NAME = 'client-app';

let clientApp: FirebaseApp | null = null;
let clientDb:  Firestore   | null = null;
let currentProjectId: string | null = null;

export async function initClientFirebase(config: ClientFirebaseConfig): Promise<Firestore> {
  // Reuse the existing connection if the project hasn't changed.
  // Destroying and recreating the app tears down the WebSocket and causes
  // every subsequent onSnapshot to start cold again.
  if (clientDb && currentProjectId === config.projectId) {
    return clientDb;
  }

  const existing = getApps().find(app => app.name === CLIENT_APP_NAME);
  if (existing) await deleteApp(existing);

  clientApp        = initializeApp(config, CLIENT_APP_NAME);
  currentProjectId = config.projectId;

  // persistentLocalCache → data loads from IndexedDB instantly on next visit.
  // persistentSingleTabManager skips the multi-tab lock negotiation so the
  // cache is available immediately instead of waiting for other tabs to yield.
  // forceOwnership: true means it claims the cache without delay.
  clientDb = initializeFirestore(clientApp, {
    localCache: persistentLocalCache({
      tabManager: persistentSingleTabManager({ forceOwnership: true }),
    }),
  });

  return clientDb;
}

export function getClientDb(): Firestore | null {
  return clientDb;
}

export async function destroyClientFirebase(): Promise<void> {
  if (clientApp) {
    await deleteApp(clientApp);
    clientApp        = null;
    clientDb         = null;
    currentProjectId = null;
  }
}
